/**
 * User Analytics Page
 * მომხმარებლის ანალიტიკის გვერდი
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Spinner, useAuth } from '@aiia/portal-core';
import { useAnalyticsOverview, useAnalyticsTraffic } from '../hooks/useAnalytics';
import { useSubscription, useUsage } from '../hooks/useSubscription';
import TrendLineChart from '../components/admin/statistics/TrendLineChart';
import ChannelBarChart from '../components/admin/statistics/ChannelBarChart';
import PeriodSelector from '../components/admin/statistics/PeriodSelector';
import type { StatisticsPeriod } from '../features/api/analytics';

function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  color,
  loading,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {loading ? (
            <Spinner size="sm" className="mt-2" />
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
              {subtitle && (
                <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
              )}
            </>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

function UsageBar({
  label,
  current,
  limit,
  unlimited,
}: {
  label: string;
  current: number;
  limit: number;
  unlimited?: boolean;
}) {
  const percentage = unlimited ? 0 : limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  const isNearLimit = percentage >= 80;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-500">
          {unlimited ? `${current} / ∞` : `${current} / ${limit}`}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${
            isNearLimit ? 'bg-red-500' : 'bg-blue-500'
          }`}
          style={{ width: unlimited ? '0%' : `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.id || '';
  const [period, setPeriod] = useState<StatisticsPeriod>('MONTH');
  const { overview, isLoading: loadingOverview } = useAnalyticsOverview();
  const { traffic, isLoading: loadingTraffic } = useAnalyticsTraffic(period);
  const { usage } = useUsage(userId);
  const { subscription } = useSubscription(userId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('analytics.title')}</h1>
          <p className="text-gray-600 mt-1">{t('analytics.subtitle')}</p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('analytics.conversations')}
          value={formatNumber(overview?.totalConversations || 0)}
          subtitle={overview?.conversationsToday ? `+${overview.conversationsToday} ${t('analytics.today')}` : undefined}
          loading={loadingOverview}
          color="bg-indigo-100 text-indigo-600"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          }
        />
        <StatCard
          title={t('analytics.messages')}
          value={formatNumber(overview?.totalMessages || 0)}
          subtitle={overview?.messagesToday ? `+${overview.messagesToday} ${t('analytics.today')}` : undefined}
          loading={loadingOverview}
          color="bg-violet-100 text-violet-600"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
        />
        <StatCard
          title={t('analytics.tokens')}
          value={formatNumber(overview?.totalTokens || 0)}
          loading={loadingOverview}
          color="bg-amber-100 text-amber-600"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
        <StatCard
          title={t('analytics.widgets')}
          value={`${overview?.activeWidgets || 0} ${t('analytics.active')}`}
          subtitle={`${overview?.totalWidgets || 0} ${t('analytics.total')}`}
          loading={loadingOverview}
          color="bg-emerald-100 text-emerald-600"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          }
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          {loadingTraffic ? (
            <div className="flex items-center justify-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <TrendLineChart
              data={traffic?.conversationTrend || []}
              title={t('analytics.conversationTrend')}
              color="#6366f1"
            />
          )}
        </Card>

        <Card className="p-6">
          {loadingTraffic ? (
            <div className="flex items-center justify-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <ChannelBarChart
              data={traffic?.conversationsByChannel || {}}
              title={t('analytics.byChannel')}
            />
          )}
        </Card>

        <Card className="p-6">
          {loadingTraffic ? (
            <div className="flex items-center justify-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <TrendLineChart
              data={traffic?.messageTrend || []}
              title={t('analytics.messageTrend')}
              color="#8b5cf6"
            />
          )}
        </Card>

        <Card className="p-6">
          {loadingTraffic ? (
            <div className="flex items-center justify-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <TrendLineChart
              data={traffic?.tokenTrend || []}
              title={t('analytics.tokenTrend')}
              color="#f59e0b"
            />
          )}
        </Card>
      </div>

      {/* Usage Limits */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('analytics.usageLimits')}</h2>
          {subscription?.planName && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
              {subscription.planName}
            </span>
          )}
        </div>
        <div className="space-y-4">
          <UsageBar
            label={t('analytics.conversations')}
            current={overview?.totalConversations || 0}
            limit={usage?.conversationsLimit || 0}
            unlimited={usage?.conversationsLimit === -1}
          />
          <UsageBar
            label={t('analytics.widgets')}
            current={overview?.totalWidgets || 0}
            limit={usage?.widgetsLimit || 0}
            unlimited={usage?.widgetsLimit === -1}
          />
          <UsageBar
            label={t('analytics.apiKeys')}
            current={overview?.totalApiKeys || 0}
            limit={usage?.apiKeysLimit || 0}
            unlimited={usage?.apiKeysLimit === -1}
          />
          <UsageBar
            label={t('analytics.dataFiles')}
            current={overview?.totalDataFiles || 0}
            limit={usage?.dataFilesLimit || 0}
            unlimited={usage?.dataFilesLimit === -1}
          />
        </div>
      </Card>
    </div>
  );
}
